/**
 * 订阅管理器 - 处理 WebSocket 订阅逻辑
 */
export abstract class SubscriptionManager<T = string> {
    // 订阅状态管理
    protected pendingSubscriptions = new Set<T>();
    protected activeSubscriptions = new Set<T>();
    protected lastSubscription?: T;

    /**
     * 订阅事件
     * @param subscriptionId 订阅标识符
     * @param sendFn 发送函数
     * @param isConnected 是否已连接
     */
    subscribe( subscriptionId: T, sendFn: ( msg: string ) => void, isConnected: boolean ) {
        if ( !subscriptionId ) {
            return;
        }

        if ( this.activeSubscriptions.has( subscriptionId ) || this.pendingSubscriptions.has( subscriptionId ) ) {
            return;
        }

        if ( !isConnected ) {
            this.pendingSubscriptions.add( subscriptionId );
            return;
        }

        this.sendSubscription( subscriptionId, sendFn );
    }

    /**
     * 取消订阅
     * @param subscriptionId 订阅标识符
     * @param sendFn 发送函数
     */
    unsubscribe( subscriptionId: T, sendFn: ( msg: string ) => void ) {
        if ( !subscriptionId ) return;
        const hadSubscription = this.activeSubscriptions.has( subscriptionId )
            || this.pendingSubscriptions.has( subscriptionId );
        this.activeSubscriptions.delete( subscriptionId );
        this.pendingSubscriptions.delete( subscriptionId );

        if ( !hadSubscription ) {
            return;
        }

        const unsubscribeMsg = this.buildUnsubscribeMessage( subscriptionId );
        sendFn( JSON.stringify( unsubscribeMsg ) );
    }

    /**
     * 处理连接成功后的订阅恢复
     * @param sendFn 发送函数
     */
    handleConnected( sendFn: ( msg: string ) => void ) {
        // 重连后按当前期望订阅集合全量重放，确保 event 和 event+guess 都能恢复
        const subscriptionsToRestore = new Set<T>( [
            ...this.activeSubscriptions,
            ...this.pendingSubscriptions,
        ] );

        this.activeSubscriptions.clear();
        this.pendingSubscriptions.clear();

        subscriptionsToRestore.forEach( ( id ) => {
            this.sendSubscription( id, sendFn );
        } );
    }

    /**
     * 处理服务器响应
     * @param data 服务器返回的数据
     * @returns 是否处理了该消息
     */
    handleMessage( data: any, _sendFn?: ( msg: string ) => void, _isConnected?: boolean ): boolean {
        const subscriptionId = this.extractSubscriptionId( data );
        if ( subscriptionId ) {
            this.pendingSubscriptions.delete( subscriptionId );
            this.activeSubscriptions.add( subscriptionId );
            return true;
        }
        return false;
    }

    /**
     * 清空所有订阅
     */
    clear() {
        this.pendingSubscriptions.clear();
        this.activeSubscriptions.clear();
        this.lastSubscription = undefined;
    }

    /**
     * 获取当前活跃订阅
     */
    getActiveSubscriptions(): Set<T> {
        return new Set( this.activeSubscriptions );
    }

    /**
     * 发送订阅消息
     */
    protected sendSubscription( subscriptionId: T, sendFn: ( msg: string ) => void ) {
        const subscribeMsg = this.buildSubscribeMessage( subscriptionId );
        sendFn( JSON.stringify( subscribeMsg ) );
        this.pendingSubscriptions.add( subscriptionId );
        this.lastSubscription = subscriptionId;
    }

    /**
     * 构建订阅消息（子类实现）
     */
    protected abstract buildSubscribeMessage( subscriptionId: T ): any;

    /**
     * 构建取消订阅消息（子类实现）
     */
    protected abstract buildUnsubscribeMessage( subscriptionId: T ): any;

    /**
     * 从服务器响应中提取订阅标识符（子类实现）
     */
    protected abstract extractSubscriptionId( data: any ): T | undefined;
}
